import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { WorkflowDetalhe, WorkflowEtapaTipo } from '../../core/models/workflow.models';
import { WorkflowsService } from '../../core/services/workflows.service';
import { WORKFLOW_ETAPAS } from '../../core/services/workflow-utils';

@Component({
  selector: 'app-workflow-board',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule],
  templateUrl: './workflow-board.component.html',
  styleUrl: './workflow-board.component.scss',
})
export class WorkflowBoardComponent implements OnInit, OnDestroy {
  workflows: WorkflowDetalhe[] = [];
  filteredWorkflows: WorkflowDetalhe[] = [];
  workflowsByEtapa = new Map<WorkflowEtapaTipo, WorkflowDetalhe[]>();
  private groupedWorkflows: Record<string, WorkflowDetalhe[]> = {};
  filterStatus: 'TODOS' | 'EM_ANDAMENTO' | 'ATRASADO' = 'TODOS';
  filterPlaca = '';
  etapas = WORKFLOW_ETAPAS;

  private subscription = new Subscription();

  constructor(private workflowsService: WorkflowsService, private router: Router) {}

  ngOnInit(): void {
    this.subscription.add(
      this.workflowsService.getAllDetails().subscribe((workflows) => {
        this.workflows = workflows;
        this.applyFilters();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilters(): void {
    const placaFiltro = this.filterPlaca.trim().toLowerCase();
    this.filteredWorkflows = this.workflows.filter((workflow) => {
      const statusMatch =
        this.filterStatus === 'TODOS' || workflow.statusGeral === this.filterStatus;
      const placaMatch = !placaFiltro || workflow.placa.toLowerCase().includes(placaFiltro);
      return statusMatch && placaMatch;
    });
    this.buildEtapaMap();
    this.rebuildGroups();
  }

  buildEtapaMap(): void {
    const map = new Map<WorkflowEtapaTipo, WorkflowDetalhe[]>();
    this.etapas.forEach((etapa) => map.set(etapa.tipo, []));
    this.filteredWorkflows.forEach((workflow) => {
      const list = map.get(workflow.etapaAtual) ?? [];
      list.push(workflow);
      map.set(workflow.etapaAtual, list);
    });
    this.workflowsByEtapa = map;
  }

  onSelectWorkflow(workflow: WorkflowDetalhe): void {
    this.router.navigate(['/workflow', workflow.id]);
  }

  trackByEtapa(_: number, etapa: { tipo: WorkflowEtapaTipo }): WorkflowEtapaTipo {
    return etapa.tipo;
  }

  dropListId(etapa: WorkflowEtapaTipo): string {
    return `drop-${String(etapa).toLowerCase().replace(/_/g, '-')}`;
  }

  get connectedDropListsIds(): string[] {
    return this.etapas.map((etapa) => this.dropListId(etapa.tipo));
  }

  getGroupedWorkflows(etapa: WorkflowEtapaTipo): WorkflowDetalhe[] {
    return this.groupedWorkflows[etapa] ?? [];
  }

  onDrop(event: CdkDragDrop<WorkflowDetalhe[]>, etapaDestino: WorkflowEtapaTipo): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      const moved = event.container.data[event.currentIndex];
      moved.etapaAtual = etapaDestino;
    }
    this.persistWorkflows();
  }

  getStageColor(etapa: WorkflowEtapaTipo): string {
    const palette = [
      '#22c55e',
      '#16a34a',
      '#60a5fa',
      '#f59e0b',
      '#f97316',
      '#fb923c',
      '#a855f7',
      '#8b5cf6',
      '#6366f1',
      '#3b82f6',
      '#1d4ed8',
      '#0f172a',
      '#334155',
    ];
    const index = this.etapas.findIndex((item) => item.tipo === etapa);
    return palette[index % palette.length];
  }

  formatOsNumero(workflow: WorkflowDetalhe): string {
    if (workflow.osNumero) {
      return workflow.osNumero;
    }
    return `OS-${String(workflow.id).padStart(6, '0')}`;
  }

  getVeiculoLabel(workflow: WorkflowDetalhe): string {
    const placa = workflow.placa || '';
    const detalhes = [workflow.marca, workflow.modelo].filter(Boolean).join(' ');
    return detalhes ? `${placa} - ${detalhes}` : placa;
  }

  getDataAbertura(workflow: WorkflowDetalhe): Date {
    return workflow.criadoEm instanceof Date ? workflow.criadoEm : new Date(workflow.criadoEm);
  }

  getDataPrevista(workflow: WorkflowDetalhe): Date | null {
    if (workflow.dataPrevistaEntrega) {
      return workflow.dataPrevistaEntrega instanceof Date
        ? workflow.dataPrevistaEntrega
        : new Date(workflow.dataPrevistaEntrega);
    }
    const created = this.getDataAbertura(workflow);
    const next = new Date(created);
    next.setDate(next.getDate() + 7);
    return next;
  }

  private rebuildGroups(): void {
    this.groupedWorkflows = this.etapas.reduce<Record<string, WorkflowDetalhe[]>>(
      (acc, etapa) => {
        acc[etapa.tipo] = this.filteredWorkflows.filter(
          (workflow) => workflow.etapaAtual === etapa.tipo
        );
        return acc;
      },
      {}
    );
  }

  private persistWorkflows(): void {
    const byId = new Map(this.workflows.map((item) => [item.id, item]));
    const used = new Set<number>();
    const ordered: WorkflowDetalhe[] = [];

    this.etapas.forEach((etapa) => {
      const group = this.groupedWorkflows[etapa.tipo] ?? [];
      group.forEach((item) => {
        const current = byId.get(item.id);
        if (!current || used.has(current.id)) {
          return;
        }
        if (current.etapaAtual !== etapa.tipo) {
          current.etapaAtual = etapa.tipo;
        }
        ordered.push(current);
        used.add(current.id);
      });
    });

    this.workflows.forEach((item) => {
      if (!used.has(item.id)) {
        ordered.push(item);
      }
    });

    this.workflows = ordered;
    this.workflowsService.setAll(ordered);
    this.applyFilters();
  }
}
